import React, { useEffect, useState } from "react";
import { createContactUs } from "../../services/FrontService";
import { decryptData } from "../../utils/crypto";
import { notify } from "../../components/Common/Alerts";
import Utils from "../../utils";
import { useLocation } from "react-router-dom";
import { useConfig } from "../../components/Context/ConfigContext";

const ContactUs = () => {
  const location = useLocation();
  const { configData } = useConfig();

  const [formdata, setFormdata] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const [errors, setErrors] = useState({});

  const type = location.state?.type;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormdata((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (configData?.contact_subject?.length) {
      setFormdata((prev) => ({
        ...prev,
        subject: type ? type : "General Inquiry",
      }));
    }
  }, [configData, type]);

  const validateForm = (data) => {
    const errors = {};

    if (!data.name.trim()) {
      errors.name = "Name is required";
    }

    if (!data.email.trim()) {
      errors.email = "Email is required";
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(data.email)) {
        errors.email = "Please enter a valid email";
      }
    }

    if (!data.subject) {
      errors.subject = "Please select a subject";
    }

    if (!data.message.trim()) {
      errors.message = "Message is required";
    }

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formdata);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({}); // clear errors
    setIsLoading(true);

    createContactUs(formdata)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        notify.success(decrypted.message);

        setFormdata({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
      })
      .catch((error) => {
        notify.error(Utils.getErrorMessage(error));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9F3] text-[#333333] font-sans">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Get in <span className="text-[#E68A00]">Touch</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions about managing your dukaan? We're here to help you
            bridge the gap to a digital future.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-[#2D2D2D]">
                Contact Information
              </h3>
              <p className="text-gray-600 mb-6">
                Fill out the form and our team will get back to you within 24
                hours.
              </p>
            </div>

            <div className="flex items-center space-x-4">
              <div className="bg-[#FFEDD5] p-3 rounded-lg text-[#E68A00]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <span className="text-lg font-medium">
                <a
                  href={`mailto:${configData?.support_email}`}
                  className="text-orange-500 font-semibold hover:underline"
                >
                  {configData?.support_email}
                </a>
              </span>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-[#F3E0C9]">
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formdata.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E68A00] focus:ring-1 focus:ring-[#E68A00] outline-none transition"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formdata.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E68A00] focus:ring-1 focus:ring-[#E68A00] outline-none transition"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Subject
                </label>
                <select
                  name="subject"
                  value={formdata.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E68A00] focus:ring-1 focus:ring-[#E68A00] outline-none transition"
                >
                  <option value="">Select Subject</option>

                  {configData?.contact_subject?.map((item, index) => (
                    <option key={index} value={item}>
                      {item}
                    </option>
                  ))}
                </select>

                {errors.subject && (
                  <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formdata.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Enter your message"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E68A00] focus:ring-1 focus:ring-[#E68A00] outline-none transition"
                />
                {errors.message && (
                  <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#E68A00] text-white font-bold py-3 rounded-lg disabled:opacity-50"
              >
                {isLoading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
